import React, { useState } from 'react';
import { Lock, Pencil, Check, X, RotateCcw } from 'lucide-react';

const obfuscate = (v) => {
  if (!v) return '';
  if (v.length <= 4) return '•'.repeat(v.length);
  return `${'•'.repeat(v.length - 4)}${v.slice(-4)}`;
};

// A config value that renders read-only until Edit is clicked. Nothing is
// written to storage while typing — only Save commits, so browser autofill
// and stray keystrokes can never silently replace the stored value.
export default function LockedField({
  label,
  value,
  onSave,
  validate,
  mask = false,
  placeholder = '',
  emptyHint = 'Not set',
  helper,
  resetLabel,
  onReset,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState('');

  const startEdit = () => {
    setDraft(value || '');
    setError('');
    setEditing(true);
  };

  const cancel = () => {
    setDraft(value || '');
    setError('');
    setEditing(false);
  };

  const commit = () => {
    const next = (draft || '').trim();
    const message = validate ? validate(next) : '';
    if (message) {
      setError(message);
      return;
    }
    onSave(next);
    setError('');
    setEditing(false);
  };

  return (
    <div>
      <label className="block text-white/40 text-[10px] font-bold tracking-widest uppercase mb-1.5">{label}</label>

      {editing ? (
        <div className="flex items-center gap-2">
          <input
            autoFocus
            type={mask ? 'password' : 'text'}
            autoComplete="new-password"
            spellCheck={false}
            value={draft}
            placeholder={placeholder}
            onChange={(e) => setDraft(e.target.value)}
            onFocus={(e) => e.target.select()}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commit();
              if (e.key === 'Escape') cancel();
            }}
            className={`flex-1 min-w-0 bg-black/40 border text-white text-xs font-mono px-3 py-2.5 outline-none ${
              error ? 'border-red-500/60' : 'border-primary/50'
            }`}
          />
          <button
            onClick={commit}
            className="shrink-0 inline-flex items-center gap-1.5 bg-primary hover:bg-primary/90 text-white text-[11px] font-bold tracking-widest px-3 py-2.5 transition-colors"
          >
            <Check className="w-3.5 h-3.5" /> SAVE
          </button>
          <button
            onClick={cancel}
            className="shrink-0 inline-flex items-center gap-1.5 border border-white/15 hover:border-white/40 text-white/70 hover:text-white text-[11px] font-bold tracking-widest px-3 py-2.5 transition-colors"
          >
            <X className="w-3.5 h-3.5" /> CANCEL
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <div className="flex-1 min-w-0 flex items-center gap-2 bg-black/40 border border-white/10 px-3 py-2.5">
            <Lock className="w-3.5 h-3.5 text-white/30 shrink-0" />
            <code className={`truncate text-xs font-mono ${value ? 'text-white' : 'text-white/30'}`}>
              {value ? (mask ? obfuscate(value) : value) : emptyHint}
            </code>
          </div>
          {resetLabel && onReset ? (
            <button
              onClick={onReset}
              className="shrink-0 inline-flex items-center gap-1.5 border border-white/10 hover:border-white/30 text-white/50 hover:text-white text-[10px] font-bold tracking-widest px-3 py-2.5 transition-colors"
            >
              <RotateCcw className="w-3 h-3" /> {resetLabel}
            </button>
          ) : null}
          <button
            onClick={startEdit}
            className="shrink-0 inline-flex items-center gap-1.5 bg-primary/15 border border-primary/30 hover:bg-primary hover:border-primary text-white text-[11px] font-bold tracking-widest px-3 py-2.5 transition-colors"
          >
            <Pencil className="w-3.5 h-3.5" /> EDIT
          </button>
        </div>
      )}

      {error ? <p className="text-red-400 text-[11px] mt-1.5">{error}</p> : null}
      {helper ? <p className="text-white/30 text-[11px] mt-2">{helper}</p> : null}
    </div>
  );
}